import React, { useEffect, useRef, useState } from 'react';
import ProfileSection from '../components/dashboard/ProfileSection';
import OrderHistory from '../components/dashboard/OrderHistory';
import { fadeIn } from '../utils/gsapAnimations';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { fetchCustomerDashboardReport } from '../utils/api';
import CustomerInsightCards from './CustomerInsightCards';

function Dashboard() {
  const pageRef = useRef(null);
  const { user, token } = useAuth();

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  useEffect(() => {
    fadeIn(pageRef.current, 0.3);
  }, []);

  useEffect(() => {
    const loadReport = async () => {
      if (!token) return;

      setReportLoading(true);
      setReportError('');

      try {
        const response = await fetchCustomerDashboardReport(token);

        if (response.status === false) {
          setReportError(response.message || 'Could not load dashboard report.');
          return;
        }

        setReport(response.data);
      } catch (err) {
        setReportError(
          err?.response?.data?.message ||
            err?.message ||
            'Could not load dashboard report.'
        );
      } finally {
        setReportLoading(false);
      }
    };

    loadReport();
  }, [token]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div ref={pageRef} className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto py-12 px-4">
        <div className="mb-8">
          <p className="text-sm uppercase tracking-[0.25em] text-primary/70 font-montserrat">
            Customer Dashboard
          </p>

          <h1 className="text-3xl md:text-5xl font-lora font-semibold text-burgundy mt-2">
            Welcome, {user?.tenant?.full_name || user?.name}
          </h1>

          <p className="text-secondary mt-3 max-w-2xl">
            Manage your profile, track your spending, and see what you buy most often.
          </p>
        </div>

        {reportError && (
          <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700">
            {reportError}
          </div>
        )}

        <CustomerInsightCards report={report} loading={reportLoading} />

        <div className="flex flex-col lg:flex-row gap-8 mt-8">
          <div className="lg:w-1/3">
            <ProfileSection />
          </div>

          <div className="lg:w-2/3 space-y-8">
            <OrderHistory
              orders={report?.recent_orders || []}
              favoriteProducts={report?.favorite_products || []}
              loading={reportLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;